import { evaluateKpi, HeadingSmall, KpiCard, KpiScoreCard, KPI_METRIC_TYPE, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import type { NewReport } from '../hooks/useNewReport';
import { localizedText, type ReportComponent } from '../definition/types';
import { metricDisplay } from '@arnold/core';
import { formatMetricNumber } from '../definition/formatMetricNumber';
import { MetricValue } from '../definition/MetricValue';
import { BandDistribution } from '../definition/BandDistribution';
import { ReportTable } from '../definition/ReportTable';
import { InlineText } from '../definition/editor/InlineText';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';
import type { OverviewEditorActions } from '../definition/editor/useOverviewEditor';
import { ReportQuestionCard } from './ReportQuestionCard';
import { AiSummary } from './AiSummary/AiSummary';
import { MetricBreakdownCard } from './MetricBreakdownCard';

export function OverviewComponent({
  report,
  component,
  actions,
  autoFocus,
}: {
  report: NewReport;
  component: ReportComponent;
  actions?: OverviewEditorActions;
  autoFocus?: boolean;
}) {
  const { t, i18n } = useTranslation('newReport');
  const { t: tKpiCard } = useTranslation('kpiCard');
  const { url } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const metrics = useDefinitionMetrics();
  const root = report.currentGroup;
  const editor = actions;
  const title = (label: Parameters<typeof localizedText>[0], placeholder: string) =>
    editor && (component.type === 'section' || component.type === 'table') ? (
      <InlineText
        value={label?.[i18n.language] ?? ''}
        placeholder={placeholder}
        size={component.type}
        autoFocus={autoFocus}
        onFocused={editor.clearFocus}
        onChange={(value) =>
          editor.updateComponent({ ...component, label: { ...component.label, [i18n.language]: value } })
        }
      />
    ) : component.type === 'section' ? (
      <SectionHeading>{localizedText(label, i18n.language)}</SectionHeading>
    ) : (
      <TableHeading>{localizedText(label, i18n.language)}</TableHeading>
    );

  if (component.type === 'section') return title(component.label, t('definition.sectionPlaceholder'));
  if (component.type === 'aiSummary')
    return <AiSummary report={report} editing={!!editor} displayMode={component.displayMode} />;
  if (component.type === 'question') return <ReportQuestionCard report={report} {...component} />;
  if (component.type === 'responseRate' || component.type === 'anonymityRate') {
    const value = root?.metrics[component.type];
    return (
      <MetricBlock>
        <h3>{t(`definition.${component.type}`)}</h3>
        <p>
          {t(
            component.type === 'responseRate' && report.rawData?.surveys.some((survey) => survey.public)
              ? 'definition.publicResponseRateHelp'
              : `definition.${component.type}Help`,
          )}
        </p>
        <MetricValue
          summary={{
            value,
            min: 0,
            max: 100,
            count: root?.answerCount ?? 0,
            status: value === undefined ? 'empty' : 'available',
          }}
          format="maxPercent"
        />
        {component.type === 'anonymityRate' && !!root?.metrics.additionallyAnonymized && (
          <small>{t('definition.additionallyAnonymized')}</small>
        )}
      </MetricBlock>
    );
  }
  if (component.type === 'metric') {
    const metric = metrics.find((item) => item.id === component.metricId);
    const summary = root?.customMetrics[component.metricId];
    const metricTitle = localizedText(metric?.title, i18n.language) || t('definition.unavailable');
    const description = localizedText(metric?.description, i18n.language);
    const openDetail = () => history.push({ pathname: `${url}/metric/${component.metricId}`, search });
    const display = summary && metric && metricDisplay(summary, metric.presentation.format);
    if (!metric || !summary || display?.value === undefined)
      return <ScoreCard title={metricTitle} subtext={description} value="—" />;
    if (metric.presentation.renderer === 'breakdown')
      return <MetricBreakdownCard report={report} metric={metric} summary={summary} baseUrl={url} />;
    if (metric.presentation.renderer === 'distribution' && summary.bands)
      return (
        <ScoreCard
          title={metricTitle}
          subtext={description}
          value={formatMetricNumber(display.value, i18n.language)}
          valueSuffix={display.suffix}
          detailRows={<BandDistribution bands={summary.bands} count={summary.count} />}
          onDetailClick={openDetail}
        />
      );
    if (metric.presentation.renderer === 'score') {
      const isScaleMetric = metric.calculation.type === 'scaleTotal';
      const scaleEvaluation = isScaleMetric ? evaluateKpi(display.value, KPI_METRIC_TYPE.SCALE, tKpiCard) : undefined;
      return (
        <ScoreCard
          title={metricTitle}
          subtext={description}
          value={formatMetricNumber(display.value, i18n.language, isScaleMetric ? 'scale' : undefined)}
          valueSuffix={display.suffix}
          state={scaleEvaluation?.state}
          badgeText={scaleEvaluation?.badgeText}
          onDetailClick={openDetail}
        />
      );
    }
    if (summary?.nps)
      return (
        <BarCard
          title={metricTitle}
          subtext={description}
          value={Math.round(display.value)}
          metricType={KPI_METRIC_TYPE.NPS}
          onDetailClick={openDetail}
        />
      );
    return (
      <BarCard
        title={metricTitle}
        subtext={description}
        value={display.value}
        min={display.min}
        max={display.max}
        metricType={KPI_METRIC_TYPE.CUSTOM}
        formattedValue={`${formatMetricNumber(display.value, i18n.language)}${display.suffix}`}
        hideBadge
        onDetailClick={openDetail}
      />
    );
  }
  if (component.type === 'table')
    return (
      <section>
        {title(component.label, t('definition.tableTitlePlaceholder'))}
        <ReportTable report={report} label={component.label} configuration={component.table} />
      </section>
    );
  return null;
}

/* Both headings keep the inline input's 4px vertical padding, so entering edit mode does not move anything. */
const SectionHeading = styled.h2`
  margin: 0;
  padding: 4px 0;
  font-family: 'Roboto';
  font-size: 22px;
  font-weight: 500;
  line-height: 120%;
  color: ${theme.colors.text.primary};
`;
const TableHeading = styled(HeadingSmall)`
  padding: 4px 0;
`;
/* Metric cards fill their grid cell; the common cards carry outer margins the grid gap already provides. */
const ScoreCard = styled(KpiScoreCard)`
  height: 100%;
  margin: 0;
`;
const BarCard = styled(KpiCard)`
  height: 100%;
  margin: 0;
  justify-content: center;
`;
const MetricBlock = styled.section`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  border: 1px solid ${theme.colors.borderSeparator.default};
  border-radius: 12px;
  background: ${theme.colors.backgroundCover.default};
  h3 {
    font-size: 20px;
    margin: 0 0 8px;
  }
  p {
    color: ${theme.colors.text.secondary};
  }
  small {
    display: block;
    margin-top: 12px;
  }
`;
